import i18n from "@/i18n";
/**
 * VVSystemRevisionDialog — TES-B2C1B1 §11-§13.
 * Effective-dated Employment Terms revision of `vv_system` via the
 * canonical B2C1A endpoint. Explicit Save only.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
    VV_SYSTEM_OPTIONS, VV_UNSUPPORTED_SYSTEMS, termsRevisionErrorMessage,
} from "./workforceHelpers";

export function buildVVRevisionPayload({ vvSystem, effectiveFrom }) {
    return { effective_from: effectiveFrom, vv_system: vvSystem };
}

export default function VVSystemRevisionDialog({
    open, onOpenChange, employeeId, restaurantId, currentVvSystem, onSaved,
}) {
    const [vvSystem, setVvSystem] = useState("");
    const [effectiveFrom, setEffectiveFrom] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setVvSystem(currentVvSystem || "");
            setEffectiveFrom("");
            setError("");
        }
    }, [open, currentVvSystem]);

    const canSave = !pending && !!vvSystem && !!effectiveFrom;

    const save = async () => {
        if (!canSave) return;
        setPending(true);
        setError("");
        try {
            await api.post(
                `/workforce/employees/${employeeId}/employment-terms`,
                buildVVRevisionPayload({ vvSystem, effectiveFrom }),
                { params: { restaurant_id: restaurantId } });
            toast.success(i18n.t("wf.vVSystemRevisionDialog.vvSystemSaved", "VV system saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            setError(termsRevisionErrorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="vv-revision-dialog"
                            className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {currentVvSystem ? i18n.t("wf.vVSystemRevisionDialog.changeVvSystem", "Change VV system") : i18n.t("wf.vVSystemRevisionDialog.setVvSystem", "Set VV system")}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.t("wf.vVSystemRevisionDialog.selectTheVvSystemThatApplies", "Select the VV system that applies to this employee's Employment Terms.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm">
                        <label htmlFor="vv-revision-system"
                                className="text-neutral-700">
                            {i18n.t("wf.vVSystemRevisionDialog.vvSystem", "VV system")}
                        </label>
                        <Select value={vvSystem} onValueChange={setVvSystem}>
                            <SelectTrigger id="vv-revision-system"
                                data-testid="vv-revision-system"
                                className="mt-1">
                                <SelectValue placeholder={i18n.t("wf.vVSystemRevisionDialog.select", "Select…")} />
                            </SelectTrigger>
                            <SelectContent>
                                {VV_SYSTEM_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}
                                        data-testid={`vv-revision-opt-${o.value}`}>
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {VV_UNSUPPORTED_SYSTEMS.has(vvSystem) && (
                            <p className="mt-1.5 text-xs text-neutral-500"
                                data-testid="vv-revision-unsupported-hint">
                                {i18n.t("wf.vVSystemRevisionDialog.miseoneDoesNotCurrentlyCalculateThis", "MiseOne does not currently calculate this VV system.")}
                            </p>
                        )}
                    </div>
                    <label className="block text-sm">
                        <span className="text-neutral-700">{i18n.t("wf.vVSystemRevisionDialog.effectiveFrom", "Effective from")}</span>
                        <Input type="date" className="mt-1"
                            data-testid="vv-revision-effective-from"
                            value={effectiveFrom}
                            onChange={(e) => setEffectiveFrom(e.target.value)} />
                        <span className="mt-1.5 block text-xs text-neutral-500">
                            {i18n.t("wf.vVSystemRevisionDialog.changesStartAtTheBeginningOf", "Changes start at the beginning of a Workforce period.")}
                        </span>
                    </label>
                    {error && (
                        <div role="alert" data-testid="vv-revision-error"
                            className="rounded-md border border-amber-200
                                        bg-amber-50 px-3 py-2 text-sm
                                        text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="vv-revision-cancel">
                        {i18n.t("common.cancel", "Cancel")}
                    </Button>
                    <Button onClick={save} disabled={!canSave}
                        data-testid="vv-revision-save">
                        {pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
